import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons/faExternalLinkAlt";
import { faPenField } from "@fortawesome/pro-solid-svg-icons/faPenField";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import FormLayout, {
  ChildLayout,
  LayoutSchemaField,
} from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import {
  getActiveEntityType,
  getEditedChildLayoutId,
  getEditedLayout,
  getEditedLayoutSortedSchema,
} from "@app/scene/settings/component/FormLayouts/store";
import {
  updateChildLayout,
  updateLayout,
} from "@app/scene/settings/component/FormLayouts/store/actions";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCalculatedField, isUniqueCustomField } from "@app/util/fieldModel/impl/assert";

import styles from "./LayoutRowActions.module.scss";
import Requirement from "./Requirement";

interface Props {
  activeChildLayout?: ChildLayout["id"];
  analyticsIssuer?: AnalyticsService;
  className?: string;
  entityType: EntityTypesSupportingFieldCustomization;
  layout?: FormLayout;
  onDelete?: (schemaField: LayoutSchemaField) => void;
  onEditDefaultValue?: (schemaField: LayoutSchemaField) => void;
  schema: Array<LayoutSchemaField>;
  schemaField: LayoutSchemaField;
  updateChildLayout: typeof updateChildLayout;
  updateLayout: typeof updateLayout;
}

const messages = defineMessages({
  edit: {
    id: "settings.formLayouts.canvas.actions.edit",
    defaultMessage: "Edit this field",
    description: "Tooltip of Edit this field action",
  },
  editDefaultValue: {
    id: "settings.formLayouts.canvas.actions.editDefaultValue",
    defaultMessage: "Edit default value",
    description: "Tooltip of Edit default value layout action",
  },
  remove: {
    id: "settings.formLayouts.canvas.actions.remove",
    defaultMessage: "Remove from layout",
    description: "Tooltip of Remove from layout action",
  },
  setDefaultValue: {
    id: "settings.formLayouts.canvas.actions.setDefaultValue",
    defaultMessage: "Set a default value",
    description: "Tooltip of Set a default value layout action",
  },
});

const LayoutRowActions: React.FC<Props> = ({
  activeChildLayout,
  analyticsIssuer,
  className,
  entityType,
  layout,
  onDelete,
  onEditDefaultValue,
  schema,
  schemaField,
  updateChildLayout,
  updateLayout,
}) => {
  const intl = useIntl();

  const activeSchema = useMemo(() => {
    if (activeChildLayout) {
      if (layout?.childLayouts?.length) {
        return layout?.childLayouts.find((child) => child.id === activeChildLayout)?.schema ?? [];
      }

      return [];
    }

    return schema;
  }, [activeChildLayout, layout?.childLayouts, schema]);

  const handleDelete = useCallback(() => {
    analyticsIssuer?.clicked(["Remove Field"]);

    const updatedSchema = activeSchema.filter(
      (schemaRow: LayoutSchemaField) =>
        schemaRow.field !== schemaField.field || schemaRow.displayOrder !== schemaField.displayOrder
    );

    if (activeChildLayout) {
      updateChildLayout({ schema: updatedSchema });
    } else {
      updateLayout({ schema: updatedSchema });
    }

    onDelete?.(schemaField);
  }, [
    activeChildLayout,
    activeSchema,
    analyticsIssuer,
    onDelete,
    schemaField,
    updateChildLayout,
    updateLayout,
  ]);

  const handleEdit = useCallback(() => {
    analyticsIssuer?.clicked(["Edit Field"]);
  }, [analyticsIssuer]);

  const handleTUpdateRequirement = useCallback(
    (updatedSchema: LayoutSchemaField[]) => {
      if (activeChildLayout) {
        updateChildLayout({
          schema: updatedSchema,
        });
      } else {
        updateLayout({
          schema: updatedSchema,
        });
      }
    },
    [activeChildLayout, updateChildLayout, updateLayout]
  );

  const getTooltipChildren = useCallback((trigger: HTMLElement) => trigger.parentElement!, []);

  const isDivider = schemaField.fieldType === "divider";

  const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);
  const isCalculated = field ? isCalculatedField(field) : false;
  const isUnique = field ? isUniqueCustomField(field) : false;
  const isSystemRequired = !!field?.isSystemRequired;
  const isTooltipDisabled = isDivider;
  const isDeleteDisabled = !field?.isDeletableInLayout && !isDivider;
  // we do not allow setting default value for activityType/funnel/stage fields in variants
  const isDefaultValueAllowed =
    field?.isDefaultValueAllowed &&
    (!activeChildLayout ||
      !field?.hasAnyFeature(FieldFeature.ACTIVITY_TYPE_FIELD, FieldFeature.FUNNEL_FIELD));
  const hasDefaultValue = !!schemaField.defaultValue?.length;

  const handleEditDefaultValue = useCallback(() => {
    onEditDefaultValue?.(schemaField);
    analyticsIssuer?.clicked(["Edit Default Value"]);
  }, [analyticsIssuer, onEditDefaultValue, schemaField]);

  if (!field && !isDivider) {
    return null;
  }

  return (
    <div className={cn(styles.container, className)}>
      {field?.schemaDetails && (
        <Tooltip
          destroyTooltipOnHide
          getPopupContainer={getTooltipChildren}
          placement="top"
          title={isTooltipDisabled ? null : intl.formatMessage(messages.edit)}
          trigger={isTooltipDisabled ? [] : ["hover"]}
        >
          <ButtonLink
            className={styles.btn}
            href={`${Path.SETTINGS}/${SettingPath.FORMS_MANAGE_FIELDS}/${entityType}/${field.schemaDetails?.id}`}
            onClick={handleEdit}
            tabIndex={-1}
            target="_blank"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </ButtonLink>
        </Tooltip>
      )}
      <Tooltip
        destroyTooltipOnHide
        getPopupContainer={getTooltipChildren}
        placement="top"
        title={isDeleteDisabled ? null : intl.formatMessage(messages.remove)}
      >
        <Button
          disabled={isDeleteDisabled}
          icon={<FontAwesomeIcon icon={faTrash} />}
          iconOnly
          onClick={handleDelete}
          tabIndex={-1}
        />
      </Tooltip>

      {isDefaultValueAllowed && (
        <Tooltip
          destroyTooltipOnHide
          getPopupContainer={getTooltipChildren}
          placement="top"
          title={intl.formatMessage(
            hasDefaultValue ? messages.editDefaultValue : messages.setDefaultValue
          )}
        >
          <Button
            icon={<FontAwesomeIcon icon={faPenField} />}
            iconOnly
            onClick={handleEditDefaultValue}
            tabIndex={-1}
          />
        </Tooltip>
      )}

      <Requirement
        activeSchema={activeSchema}
        analyticsIssuer={analyticsIssuer}
        isCalculated={isCalculated}
        isSystemRequired={isSystemRequired}
        isUnique={isUnique}
        onUpdateSchema={handleTUpdateRequirement}
        row={schemaField}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeChildLayout: getEditedChildLayoutId(state),
  entityType: getActiveEntityType(state),
  layout: getEditedLayout(state),
  schema: getEditedLayoutSortedSchema(state),
});

const mapDispatchToProps = {
  updateChildLayout,
  updateLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutRowActions);
